import { Button, ButtonType } from '@resurs-internal/styx-react'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '../common/utils'
import styles from './Mock.module.scss'

const proxyPath = process.env.REACT_APP_RETAIL_FINANCE_MOCK_PROXY_PATH
const sessionPath = sessionId =>
  `${proxyPath}/consumer_credit_api/v1/open-banking/sessions/${encodeURIComponent(
    sessionId,
  )}`

const TERMINAL_STATES = ['COMPLETED', 'FAILED', 'EXPIRED']

const getResponseBody = async response => {
  let body = null
  try {
    body = await response.json()
  } catch (error) {
    // Some proxy errors have no JSON body.
  }

  if (!response.ok) {
    throw new Error(
      body?.message ||
        body?.error ||
        body?.detail ||
        `Open Banking request failed (${response.status})`,
    )
  }

  return body
}

const request = (url, options = {}) =>
  fetch(url, {
    headers: { Accept: 'application/json', ...options.headers },
    ...options,
  }).then(getResponseBody)

const formatValue = value => (value == null || value === '' ? '—' : value)

export const OpenBankingMock = () => {
  const { sessionId: pathSessionId } = useParams()
  const { sessionId: querySessionId } = useQuery()
  const sessionId = pathSessionId || querySessionId
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busyAction, setBusyAction] = useState(null)
  const [error, setError] = useState(null)

  const loadSession = () => {
    if (!sessionId) {
      setError('No Open Banking session ID was provided.')
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    request(`${sessionPath(sessionId)}/start`)
      .catch(startError =>
        request(sessionPath(sessionId)).catch(() => {
          throw startError
        }),
      )
      .then(setSession)
      .catch(setError)
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadSession()
    // The session ID is the only value that should trigger a new load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId])

  const goToReturnUrl = nextSession => {
    const returnUrl =
      nextSession.state === 'COMPLETED'
        ? nextSession.returnUri
        : nextSession.failureReturnUri
    if (returnUrl) window.location.href = returnUrl
  }

  const performAction = (action, endpoint, body, redirect = false) => {
    setBusyAction(action)
    setError(null)
    request(`${sessionPath(sessionId)}/${endpoint}`, {
      method: 'POST',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
      .then(nextSession => {
        setSession(nextSession)
        if (redirect) goToReturnUrl(nextSession)
      })
      .catch(setError)
      .finally(() => setBusyAction(null))
  }

  const isTerminal = TERMINAL_STATES.includes(session?.state)
  const isExpired = session?.state === 'EXPIRED'
  const isFailed = session?.state === 'FAILED'
  const isCompleted = session?.state === 'COMPLETED'
  const canReturn =
    (isCompleted && session?.returnUri) ||
    ((isFailed || isExpired) && session?.failureReturnUri)

  if (loading) return <p>Loading Open Banking session…</p>

  if (error && !session) {
    return (
      <>
        <h1>Open Banking unavailable</h1>
        <p role="alert">{error.message || error}</p>
        <div className={styles.lineWrapper}>
          <Button fullWidth onClick={loadSession}>
            Try Again
          </Button>
        </div>
      </>
    )
  }

  if (!session) return null

  return (
    <>
      <h1>Open Banking</h1>
      <p>
        Mock provider session. Choose how the customer responds to the consent
        request.
      </p>

      {error && (
        <div className={styles.infoSection} role="alert">
          <div className={styles.listItem}>
            <span className={styles.label}>Error</span>
            <span>{error.message || error}</span>
          </div>
        </div>
      )}

      <div className={styles.infoSection}>
        <div className={styles.sectionLabel}>Provider session</div>
        <div className={styles.listItem}>
          <span className={styles.label}>Provider</span>
          <span>{formatValue(session.provider)}</span>
        </div>
        <div className={styles.listItem}>
          <span className={styles.label}>Session ID</span>
          <span className={styles.mono}>
            {formatValue(session.sessionId || sessionId)}
          </span>
        </div>
        <div className={styles.listItem}>
          <span className={styles.label}>Application case</span>
          <span className={styles.mono}>
            {formatValue(session.applicationCaseId)}
          </span>
        </div>
        <div className={styles.listItem}>
          <span className={styles.label}>Applicant</span>
          <span>{formatValue(session.applicantType)}</span>
        </div>
        <div className={styles.listItem}>
          <span className={styles.label}>Government ID</span>
          <span className={styles.mono}>
            {formatValue(session.governmentId)}
          </span>
        </div>
        <div className={styles.listItem}>
          <span className={styles.label}>Session state</span>
          <span>{formatValue(session.state)}</span>
        </div>
        <div className={styles.listItem}>
          <span className={styles.label}>Financial profile</span>
          <span>{formatValue(session.profile)}</span>
        </div>
        {session.failure && (
          <div className={styles.listItem}>
            <span className={styles.label}>Configured failure</span>
            <span>{session.failure}</span>
          </div>
        )}
        {session.failureReason && (
          <div className={styles.listItem}>
            <span className={styles.label}>Failure reason</span>
            <span>{session.failureReason}</span>
          </div>
        )}
      </div>

      {isExpired && (
        <p role="status">
          This Open Banking session has expired. No further action is possible.
        </p>
      )}
      {isFailed && (
        <p role="status">
          The Open Banking session failed. You can return to the calling
          application.
        </p>
      )}
      {isCompleted && (
        <p role="status">
          Open Banking completed successfully. You can return to the calling
          application.
        </p>
      )}
      {session.state === 'CLASSIFYING' && (
        <p role="status">The financial information is being classified.</p>
      )}

      {!isTerminal && session.state !== 'CLASSIFYING' && (
        <>
          <div className={styles.lineWrapper}>
            <Button
              id="grantConsentButton"
              fullWidth
              disabled={busyAction != null}
              onClick={() =>
                performAction('grant', 'consent-return', { consentGiven: true })
              }
            >
              Grant Consent
            </Button>
          </div>
          <div className={styles.lineWrapper}>
            <Button
              id="declineConsentButton"
              type={ButtonType.Secondary}
              fullWidth
              disabled={busyAction != null}
              onClick={() =>
                performAction(
                  'decline',
                  'consent-return',
                  { consentGiven: false },
                  true,
                )
              }
            >
              Decline Consent
            </Button>
          </div>
          <div className={styles.lineWrapper}>
            <Button
              id="completeButton"
              fullWidth
              disabled={busyAction != null}
              onClick={() =>
                performAction('complete', 'complete', undefined, true)
              }
            >
              Complete
            </Button>
          </div>
          <div className={styles.lineWrapper}>
            <Button
              id="technicalFailureButton"
              type={ButtonType.Secondary}
              fullWidth
              disabled={busyAction != null}
              onClick={() =>
                performAction(
                  'technical-failure',
                  'fail',
                  { reason: 'TECHNICAL_FAILURE' },
                  true,
                )
              }
            >
              Technical Failure
            </Button>
          </div>
          <div className={styles.lineWrapper}>
            <Button
              id="cancelButton"
              type={ButtonType.Secondary}
              fullWidth
              disabled={busyAction != null}
              onClick={() => performAction('cancel', 'cancel', undefined, true)}
            >
              Cancel
            </Button>
          </div>
        </>
      )}

      {isTerminal && canReturn && (
        <div className={styles.lineWrapper}>
          <Button fullWidth onClick={() => goToReturnUrl(session)}>
            Continue
          </Button>
        </div>
      )}
    </>
  )
}
